import { useEffect, useState } from 'react';
import { layoutDay } from './programModel';

// The program on the day of the event: which day is today and whether today's program runs. Nothing
// is marked by the clock inside a day (a congress runs behind its printed times). Program times are
// wall-clock times at the venue, so "now" is read in the event's time zone (program.event.timeZone,
// Europe/Vienna by default), whatever the device's zone.

export const VENUE_TIME_ZONE = 'Europe/Vienna';

// The anchor of an event page's address that makes it open at today's program once it is shown
// (ProgramSectionView): where the other pages' "Live" button (LiveProgramButton) leads.
export const LIVE_HASH = '#live';
const DAY_END = 24 * 60;

// "09:00:00" or "09:00" -> 540; null without a time.
export const minutesOf = (t) => {
  if (!t) return null;
  const [h, m] = String(t).split(':').map(Number);
  return Number.isFinite(h) && Number.isFinite(m) ? h * 60 + m : null;
};

const partsAt = (timeZone, date) => new Intl.DateTimeFormat('en-CA', {
  timeZone, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
}).formatToParts(date);

// The date ("YYYY-MM-DD") and the minute of the day at the venue at `date`.
export const venueNow = (timeZone, date = new Date()) => {
  let parts;
  try {
    parts = partsAt(timeZone || VENUE_TIME_ZONE, date);
  } catch (e) {
    parts = partsAt(VENUE_TIME_ZONE, date); // an unknown zone name
  }
  const get = (type) => parts.find((p) => p.type === type)?.value;
  return {
    day: `${get('year')}-${get('month')}-${get('day')}`,
    minutes: ((Number(get('hour')) % 24) * 60) + Number(get('minute')),
  };
};

// The venue's date and minute, recomputed at every full minute (and when the tab is shown again, as a
// hidden tab's timers are slowed down); null while `enabled` is false (the print copy).
export const useVenueNow = (timeZone, enabled = true) => {
  const [now, setNow] = useState(() => (enabled ? venueNow(timeZone) : null));
  useEffect(() => {
    if (!enabled) {
      setNow(null);
      return undefined;
    }
    let timer;
    const tick = () => {
      clearTimeout(timer);
      setNow(venueNow(timeZone));
      timer = setTimeout(tick, 60000 - (Date.now() % 60000));
    };
    const onVisible = () => { if (document.visibilityState === 'visible') tick(); };
    tick();
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [timeZone, enabled]);
  return now;
};

// Where an entry ends: its own end time, else the next later start among its siblings, else the end
// of the day.
const endOf = (entry, siblings) => {
  const own = minutesOf(entry.endTime);
  if (own != null) return own;
  const start = minutesOf(entry.startTime);
  const later = siblings.map((s) => minutesOf(s.startTime)).filter((m) => m != null && start != null && m > start);
  return later.length > 0 ? Math.min(...later) : DAY_END;
};

// Whether the program of `day` runs at `minutes` past midnight (venue time): its first slot has begun
// and the last one has not ended.
export const dayRuns = (program, day, minutes) => {
  const { slots } = layoutDay(program, day);
  if (slots.length === 0) return false;
  const top = slots.flatMap((s) => s.entries);
  const first = minutesOf(slots[0].time);
  const last = Math.max(...slots[slots.length - 1].entries.map((e) => endOf(e, top)));
  return first != null && first <= minutes && minutes < last;
};
